import React, { useState, useEffect, useRef } from "react";

const MAX_WAIT_SEC = 60;

export function CheckWidgetModal({ isOpen, onClose, agentId, agentDomain, token, t, apiUrl }) {
  const [status, setStatus] = useState("idle"); // idle | checking | success | error | timeout | rate_limited
  const [message, setMessage] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const cancelledRef = useRef(false);
  const timeoutTriggeredRef = useRef(false);

  useEffect(() => {
    if (!isOpen || !agentId || !token) return;

    cancelledRef.current = false;
    timeoutTriggeredRef.current = false;
    setStatus("checking");
    setMessage("");
    setElapsed(0);
    startTimeRef.current = Date.now();

    const runCheck = async () => {
      try {
        const res = await fetch(`${apiUrl}/api/check-widget-installation/${agentId}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (cancelledRef.current) return;

        const data = await res.json().catch(() => ({}));

        if (res.status === 429) {
          setStatus("rate_limited");
          setMessage(t("Too many checks. Please try again in a few minutes."));
          return;
        }

        if (!res.ok) {
          setStatus("error");
          setMessage(data.detail || t("Check failed"));
          return;
        }

        if (data.ok) {
          setStatus("success");
          setMessage(t("Widget is correctly installed on your site."));
        } else {
          setStatus("error");
          const msgMap = {
            no_domain: t("No domain configured for this agent."),
            not_found: t("Widget script was not found on the page. Please install it and try again."),
            timeout: t("The site did not respond in time. Try again later."),
            fetch_error: t("Could not access the site. Check that the domain is correct and the site is online."),
            invalid_domain: t("Invalid domain in agent settings."),
          };
          setMessage(msgMap[data.message] || t("Widget was not found on the page."));
        }
      } catch (err) {
        if (cancelledRef.current) return;
        setStatus("error");
        setMessage(err.message || t("Check failed"));
      }
    };

    runCheck();

    const tick = () => {
      if (cancelledRef.current) return;
      const now = Date.now();
      const sec = Math.floor((now - startTimeRef.current) / 1000);
      setElapsed(sec);
      if (sec >= MAX_WAIT_SEC && !timeoutTriggeredRef.current) {
        timeoutTriggeredRef.current = true;
        setStatus("timeout");
        setMessage(t("Check timed out. The site may be slow. Try again later."));
      }
    };

    timerRef.current = setInterval(tick, 1000);

    return () => {
      cancelledRef.current = true;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isOpen, agentId, token, apiUrl, t]);

  useEffect(() => {
    if (status !== "checking") {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [status]);

  if (!isOpen) return null;

  const isDone = ["success", "error", "timeout", "rate_limited"].includes(status);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="check-widget-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="check-widget-modal-title">
          {t("Check widget installation")}
        </h3>
        {agentDomain && (
          <p className="check-widget-modal-domain">{t("Domain")}: {agentDomain}</p>
        )}
        {status === "checking" && (
          <>
            <p className="check-widget-modal-hint">
              {t("Checking... This may take up to a minute.")}
            </p>
            <div className="check-widget-modal-timer">
              {elapsed}s / {MAX_WAIT_SEC}s
            </div>
            <div className="check-widget-modal-spinner" />
          </>
        )}
        {status === "success" && (
          <div className="check-widget-result check-widget-result--success">
            <span className="check-widget-icon">✓</span>
            <p>{message}</p>
          </div>
        )}
        {(status === "error" || status === "timeout" || status === "rate_limited") && (
          <div className="check-widget-result check-widget-result--error">
            <span className="check-widget-icon">✗</span>
            <p>{message}</p>
          </div>
        )}
        <div className="check-widget-modal-actions">
          <button type="button" onClick={onClose}>
            {isDone ? t("Close") : t("Cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}
